/**
 * Admin — Formadores do curso + Avaliação de Reação do Formador (Mod. 16).
 *
 * Página dedicada com:
 *  - Lista/CRUD de formadores do curso
 *  - Envio de link anónimo por email a cada formador
 *  - Sumário das respostas (média por pergunta)
 *  - Preenchimento manual pelo admin (fallback)
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  UserCog, Loader2, Plus, Trash2, Mail, Send, Save, ExternalLink,
  Search, X, RefreshCcw, CheckCircle2, Copy, Pencil,
} from "lucide-react";
import { adminApi } from "../api";
import { toast } from "sonner";
import CursoTabsNav from "../components/CursoTabsNav";

export default function AdminCursoFormadores() {
  const [params] = useSearchParams();
  const cursoId = params.get("curso") || "";
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [modalAdd, setModalAdd] = useState(false);
  const [busy, setBusy] = useState("");
  const [expanded, setExpanded] = useState({});
  const [drafts, setDrafts] = useState({});

  const load = useCallback(async () => {
    if (!cursoId) return;
    setLoading(true); setError("");
    try {
      const [list, sum, q] = await Promise.all([
        adminApi.cursoFormadoresList(cursoId),
        adminApi.cursoAvaliacaoFormadorList(cursoId),
        adminApi.cursoAvaliacaoFormadorQuestions(cursoId),
      ]);
      setData(list);
      setSummary(sum);
      setQuestions(q.questions || []);
      // seed drafts a partir do sumário para preenchimento manual
      const seed = {};
      (sum.formadores || []).forEach((f) => {
        seed[f.id] = {
          answers: { ...f.answers },
          sugestoes: f.sugestoes || "",
        };
      });
      setDrafts(seed);
    } catch (e) {
      setError(e.message || "Erro a carregar formadores");
    } finally { setLoading(false); }
  }, [cursoId]);

  useEffect(() => { load(); }, [load]);

  const onDelete = async (fid, nome) => {
    if (!window.confirm(`Remover ${nome} do curso?`)) return;
    setBusy(`del-${fid}`);
    try {
      await adminApi.cursoFormadorDelete(cursoId, fid);
      toast.success("Formador removido");
      await load();
    } catch (e) { toast.error(e.message || "Erro a remover"); }
    finally { setBusy(""); }
  };

  const onEnviarTodos = async (forceResend = false) => {
    const msg = forceResend
      ? "REENVIAR os emails a TODOS os formadores (incluindo quem já recebeu)?"
      : "Enviar email com o link de avaliação aos formadores que ainda não receberam?";
    if (!window.confirm(msg)) return;
    setBusy("enviar-todos");
    try {
      const r = await adminApi.cursoAvaliacaoFormadorEnviarEmails(cursoId, forceResend);
      if (r.sent === 0 && !forceResend) {
        toast.info("Nada a enviar — todos os formadores já receberam. Usa 'Reenviar' para forçar.");
      } else {
        toast.success(`${r.sent} emails enviados.`);
      }
      if (r.no_email) toast.warning(`${r.no_email} formadores sem email.`);
      await load();
    } catch (e) { toast.error(e.message || "Erro a enviar emails"); }
    finally { setBusy(""); }
  };

  const onReenviarUnico = async (fid) => {
    setBusy(`reenviar-${fid}`);
    try {
      const r = await adminApi.cursoAvaliacaoFormadorReenviar(cursoId, fid);
      if (r.sent === 0) toast.warning("Sem email atribuído — actualiza o formador primeiro.");
      else toast.success("Link reenviado.");
      await load();
    } catch (e) { toast.error(e.message || "Erro a reenviar"); }
    finally { setBusy(""); }
  };

  const copyLink = async (token) => {
    if (!token) {
      toast.error("Ainda sem token — envia primeiro pelo menos 1 email para gerar tokens.");
      return;
    }
    const url = `${window.location.origin}/cursos/avaliacao-formador/${token}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copiado.");
    } catch { toast.error("Falha a copiar link."); }
  };

  const gravarManual = async (fid) => {
    const d = drafts[fid];
    if (!d) return;
    const missing = questions.filter((q) => !d.answers[q.key]).length;
    if (missing > 0) {
      toast.error(`Faltam ${missing} respostas obrigatórias.`);
      return;
    }
    setBusy(`gravar-${fid}`);
    try {
      await adminApi.cursoAvaliacaoFormadorSet(cursoId, fid, {
        answers: d.answers,
        sugestoes: d.sugestoes || null,
      });
      toast.success("Resposta gravada");
      await load();
    } catch (e) { toast.error(e.message || "Erro a gravar"); }
    finally { setBusy(""); }
  };

  const formadoresAtivos = useMemo(
    () => (data?.formadores || []).filter((f) => f.activo),
    [data],
  );

  if (!cursoId) {
    return <div className="p-6 text-[#6E6754]">Falta o parâmetro <code>?curso=&lt;ID&gt;</code> no URL.</div>;
  }

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto" data-testid="admin-curso-formadores-page">
      <CursoTabsNav cursoId={cursoId} current="formadores" />

      <header className="mb-4 flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-[#F3F0E7] grid place-items-center">
          <UserCog className="w-5 h-5 text-[#A8843C]" />
        </div>
        <div className="flex-1">
          <h1 className="text-xl font-semibold text-[#1B1B1B]">Formadores do curso #{cursoId}</h1>
          <p className="text-sm text-[#6E6754]">
            Gestão dos formadores + envio do link anónimo para preenchimento do
            Mod. 16 (Avaliação de Reação do Formador).
          </p>
        </div>
        <button onClick={load} className="text-xs text-[#6B7280] hover:text-[#0F1829] inline-flex items-center gap-1"
                data-testid="formadores-reload">
          <RefreshCcw size={12} /> Recarregar
        </button>
      </header>

      {loading && (
        <div className="flex items-center gap-2 py-6 text-[#6E6754]">
          <Loader2 className="w-4 h-4 animate-spin" /> A carregar…
        </div>
      )}
      {error && !loading && (
        <div className="p-3 mb-4 bg-red-50 border border-red-200 text-red-800 text-sm rounded">{error}</div>
      )}

      {!loading && !error && data && summary && (
        <>
          {/* KPI + acções */}
          <div className="grid grid-cols-4 gap-3 mb-4">
            <Kpi label="Total formadores" value={data.total_formadores} testid="kpi-formadores-total" />
            <Kpi label="Respostas Mod. 16" value={data.total_respostas} testid="kpi-respostas" />
            <Kpi label="Emails enviados" value={data.total_emails_enviados} testid="kpi-emails-enviados" />
            <Kpi label="Média global" value={summary.resumo?.overall_avg != null ? summary.resumo.overall_avg.toFixed(2) : "—"}
                 sub="(1-5)" testid="kpi-media-global" />
          </div>

          <div className="flex flex-wrap gap-2 mb-4">
            <button
              onClick={() => setModalAdd(true)}
              className="text-xs px-3 py-1.5 bg-[#A8843C] text-white rounded hover:bg-[#8f6e2f] inline-flex items-center gap-1.5"
              data-testid="btn-add-formador"
            >
              <Plus size={12} /> Adicionar formador
            </button>
            <button
              onClick={() => onEnviarTodos(false)}
              disabled={!!busy || formadoresAtivos.length === 0}
              className="text-xs px-3 py-1.5 bg-[#0F1829] text-white rounded hover:bg-[#1A2540] disabled:opacity-50 inline-flex items-center gap-1.5"
              data-testid="btn-enviar-emails"
            >
              {busy === "enviar-todos" ? <Loader2 className="animate-spin" size={12} /> : <Mail size={12} />}
              Enviar link a todos os formadores
            </button>
            <button
              onClick={() => onEnviarTodos(true)}
              disabled={!!busy || formadoresAtivos.length === 0}
              className="text-xs px-3 py-1.5 bg-white border border-[#E6E1D6] text-[#6E6754] rounded hover:bg-[#FAF8F2] disabled:opacity-50 inline-flex items-center gap-1.5"
              data-testid="btn-reenviar-emails"
            >
              <Send size={12} /> Reenviar a todos (forçado)
            </button>
          </div>

          {/* Lista formadores */}
          {formadoresAtivos.length === 0 ? (
            <div className="p-6 bg-white border border-[#E6E1D6] rounded-md text-center">
              <p className="text-sm text-[#6E6754] mb-3">Ainda sem formadores neste curso.</p>
              <button onClick={() => setModalAdd(true)}
                      className="text-xs px-3 py-1.5 bg-[#A8843C] text-white rounded hover:bg-[#8f6e2f] inline-flex items-center gap-1.5">
                <Plus size={12} /> Adicionar o primeiro formador
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              {formadoresAtivos.map((f) => {
                const isOpen = expanded[f.id];
                const draft = drafts[f.id] || { answers: {}, sugestoes: "" };
                const filled = questions.filter((q) => draft.answers[q.key]).length;
                return (
                  <div key={f.id} className="bg-white border border-[#E6E1D6] rounded"
                       data-testid={`formador-row-${f.id}`}>
                    {/* header row */}
                    <div className="p-3 flex items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-[#0F1829] truncate">{f.nome}</p>
                        <p className="text-xs text-[#6E6754] truncate">
                          {f.email || <span className="italic text-[#B8AC8A]">sem email</span>}
                          {f.nif && <span className="ml-2 text-[10px] text-[#978D78]">NIF: {f.nif}</span>}
                        </p>
                      </div>
                      {f.submissao ? (
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#E6F4EA] text-[#0A6B2E]"
                              title={`Submetido em ${new Date(f.submissao).toLocaleString("pt-PT")}`}>
                          <CheckCircle2 className="inline w-3 h-3 mr-0.5" /> respondeu
                        </span>
                      ) : (
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#FFF7E6] text-[#7A5C18]">
                          por responder
                        </span>
                      )}
                      {f.email_enviado && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#F3F0E7] text-[#6E6754]"
                              title={`Email enviado em ${new Date(f.data_email_enviado).toLocaleString("pt-PT")}`}>
                          email ✓
                        </span>
                      )}
                      <button
                        onClick={() => copyLink(f.token)}
                        disabled={!f.token}
                        className="text-[10px] px-2 py-1 rounded bg-white border border-[#E6E1D6] hover:bg-[#FAF8F2] inline-flex items-center gap-1 disabled:opacity-40"
                        data-testid={`btn-copy-link-${f.id}`}
                        title={f.token ? "Copiar link" : "Sem token — envie primeiro o email"}
                      >
                        <Copy size={10} /> Link
                      </button>
                      <button
                        onClick={() => onReenviarUnico(f.id)}
                        disabled={busy === `reenviar-${f.id}` || !f.email}
                        className="text-[10px] px-2 py-1 rounded bg-white border border-[#E6E1D6] hover:bg-[#FAF8F2] inline-flex items-center gap-1 disabled:opacity-40"
                        data-testid={`btn-reenviar-${f.id}`}
                        title={f.email ? "Reenviar link por email" : "Sem email"}
                      >
                        {busy === `reenviar-${f.id}` ? <Loader2 className="animate-spin" size={10} /> : <Send size={10} />}
                      </button>
                      <button
                        onClick={() => setExpanded((s) => ({ ...s, [f.id]: !s[f.id] }))}
                        className="text-[10px] px-2 py-1 rounded bg-white border border-[#E6E1D6] hover:bg-[#FAF8F2] inline-flex items-center gap-1"
                        data-testid={`btn-toggle-${f.id}`}
                      >
                        <Pencil size={10} /> {isOpen ? "Fechar" : "Preencher"}
                      </button>
                      <button
                        onClick={() => onDelete(f.id, f.nome)}
                        disabled={busy === `del-${f.id}`}
                        className="text-[10px] px-2 py-1 rounded bg-white border border-[#FCA5A5] text-[#9B1C1C] hover:bg-[#FDE8E8] inline-flex items-center gap-1 disabled:opacity-40"
                        data-testid={`btn-del-${f.id}`}
                      >
                        {busy === `del-${f.id}` ? <Loader2 className="animate-spin" size={10} /> : <Trash2 size={10} />}
                      </button>
                    </div>
                    {/* expand: preenchimento manual */}
                    {isOpen && (
                      <div className="border-t border-[#F3F0E7] p-3 bg-[#FAF8F2] space-y-3">
                        <p className="text-[11px] text-[#6E6754]">
                          Preenchimento manual pelo admin (opcional — normalmente é o próprio
                          formador que preenche via link).
                        </p>
                        <div className="space-y-1.5">
                          {questions.map((q) => (
                            <div key={q.key} className="grid grid-cols-12 gap-2 items-center text-xs py-1 border-b border-[#F3F0E7] last:border-b-0">
                              <span className="col-span-5 font-medium text-[#0F1829]">{q.label}</span>
                              <span className="col-span-2 text-[10px] text-[#978D78] text-right pr-1">{q.low}</span>
                              <div className="col-span-3 flex gap-1 justify-center">
                                {[1, 2, 3, 4, 5].map((n) => (
                                  <button
                                    key={n}
                                    type="button"
                                    onClick={() => setDrafts((s) => ({
                                      ...s,
                                      [f.id]: {
                                        ...(s[f.id] || { answers: {}, sugestoes: "" }),
                                        answers: {
                                          ...(s[f.id]?.answers || {}),
                                          [q.key]: n,
                                        },
                                      },
                                    }))}
                                    className={`w-7 h-7 rounded text-xs font-bold border transition ${
                                      draft.answers[q.key] === n
                                        ? "bg-[#0F1829] text-white border-[#0F1829]"
                                        : "bg-white text-[#6E6754] border-[#E6E1D6] hover:border-[#0F1829]"
                                    }`}
                                    data-testid={`scale-${f.id}-${q.key}-${n}`}
                                  >{n}</button>
                                ))}
                              </div>
                              <span className="col-span-2 text-[10px] text-[#978D78] pl-1">{q.high}</span>
                            </div>
                          ))}
                        </div>
                        <textarea
                          value={draft.sugestoes}
                          onChange={(e) => setDrafts((s) => ({
                            ...s, [f.id]: { ...(s[f.id] || { answers: {} }), sugestoes: e.target.value },
                          }))}
                          rows={2}
                          placeholder="Sugestões / Críticas (opcional)"
                          className="w-full border border-[#E6E1D6] rounded px-2 py-1 text-xs bg-white focus:border-[#A8843C] focus:outline-none resize-y"
                          data-testid={`sugestoes-${f.id}`}
                        />
                        <div className="flex items-center justify-between">
                          <p className="text-[10px] text-[#978D78]">
                            {filled}/{questions.length} respostas preenchidas.
                          </p>
                          <button
                            onClick={() => gravarManual(f.id)}
                            disabled={busy === `gravar-${f.id}`}
                            className="text-xs px-3 py-1.5 bg-[#A8843C] text-white rounded hover:bg-[#8f6e2f] disabled:opacity-50 inline-flex items-center gap-1.5"
                            data-testid={`btn-gravar-${f.id}`}
                          >
                            {busy === `gravar-${f.id}` ? <Loader2 className="animate-spin" size={12} /> : <Save size={12} />}
                            Gravar (manual)
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Resumo por pergunta */}
          {summary.total_respostas > 0 && (
            <section className="mt-6 p-4 bg-white border border-[#E6E1D6] rounded-md" data-testid="resumo-mod16">
              <h2 className="text-sm font-bold uppercase tracking-[0.12em] mb-3">
                Resumo — média por pergunta ({summary.total_respostas} respostas)
              </h2>
              <ul className="space-y-1.5">
                {questions.map((q) => {
                  const pq = summary.resumo.questions[q.key];
                  const avg = pq?.avg;
                  const pct = avg != null ? (avg / 5) * 100 : 0;
                  return (
                    <li key={q.key} className="grid grid-cols-12 gap-2 items-center text-xs py-1 border-b border-[#F3F0E7] last:border-b-0">
                      <span className="col-span-5 text-[#0F1829]">{q.label}</span>
                      <span className="col-span-2 font-bold text-[#0F1829]">
                        {avg != null ? avg.toFixed(2) : "—"}
                        <span className="text-[10px] text-[#978D78] ml-1">({pq?.count || 0})</span>
                      </span>
                      <div className="col-span-5 h-3 rounded bg-[#F3F0E7] relative overflow-hidden">
                        <div className="absolute inset-y-0 left-0 bg-[#0F1829]" style={{ width: `${pct}%` }} />
                      </div>
                    </li>
                  );
                })}
              </ul>
            </section>
          )}
        </>
      )}

      {modalAdd && (
        <AddFormadorModal
          cursoId={cursoId}
          onClose={() => setModalAdd(false)}
          onSaved={() => { setModalAdd(false); load(); }}
        />
      )}
    </div>
  );
}


function Kpi({ label, value, sub, testid }) {
  return (
    <div className="p-3 bg-white border border-[#E6E1D6] rounded-md" data-testid={testid}>
      <p className="text-[10px] uppercase tracking-wider text-[#978D78]">{label}</p>
      <p className="text-xl font-bold text-[#0F1829]">{value}</p>
      {sub && <p className="text-[10px] text-[#978D78]">{sub}</p>}
    </div>
  );
}


function AddFormadorModal({ cursoId, onClose, onSaved }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(null); // id_formador em curso

  const load = async (query = "") => {
    setLoading(true);
    try {
      const r = await adminApi.cursoFormadoresAvailable(cursoId, query);
      setResults(r.results || []);
    } catch (e) { toast.error(e.message || "Erro"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(""); }, []); // initial load only

  useEffect(() => {
    const t = setTimeout(() => { load(q); }, 300);
    return () => clearTimeout(t);
  }, [q]); // search-triggered reload

  const pick = async (r) => {
    setAdding(r.id_formador);
    try {
      await adminApi.cursoFormadorAdd(cursoId, r.id_formador);
      toast.success(`${r.nome} adicionado(a) ao curso.`);
      onSaved();
    } catch (e) { toast.error(e.message || "Erro a adicionar"); }
    finally { setAdding(null); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}
         data-testid="modal-add-formador">
      <div className="bg-white rounded-lg shadow-lg max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <header className="p-4 border-b border-[#E6E1D6] flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-[#0F1829]">Adicionar formador ao curso</h3>
            <p className="text-[10px] text-[#978D78] mt-0.5">
              Escolhe da biblioteca de formadores. Para adicionar um formador novo,{" "}
              <a href="/admin/formadores" target="_blank" rel="noreferrer"
                 className="text-[#A8843C] font-semibold hover:underline">
                Manutenção de Formadores ↗
              </a>.
            </p>
          </div>
          <button onClick={onClose} className="text-[#6E6754] hover:text-[#0F1829]"><X size={16} /></button>
        </header>
        <div className="p-4 space-y-3">
          <div className="relative">
            <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-[#978D78]" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Procurar por nome, email ou NIF…"
              className="w-full pl-7 pr-2 py-1.5 text-sm border border-[#E6E1D6] rounded focus:border-[#A8843C] focus:outline-none"
              autoFocus
              data-testid="search-input"
            />
          </div>
          {loading ? (
            <p className="text-xs text-[#6E6754] flex items-center gap-2 p-2">
              <Loader2 className="animate-spin" size={12} /> A carregar…
            </p>
          ) : results.length === 0 ? (
            <p className="text-xs text-[#978D78] italic p-3 text-center bg-[#FAF8F2] rounded">
              {q ? "Sem resultados na biblioteca." : "Todos os formadores da biblioteca já foram adicionados a este curso."}
              <br />
              <a href="/admin/formadores" target="_blank" rel="noreferrer"
                 className="text-[#A8843C] font-semibold hover:underline mt-2 inline-block">
                → Criar novo formador na biblioteca
              </a>
            </p>
          ) : (
            <ul className="max-h-72 overflow-y-auto space-y-1">
              {results.map((r) => (
                <li key={r.id_formador}>
                  <button
                    onClick={() => pick(r)}
                    disabled={adding === r.id_formador}
                    className="w-full text-left p-2.5 text-xs border border-[#E6E1D6] rounded hover:bg-[#FAF8F2] hover:border-[#A8843C] transition disabled:opacity-60 flex items-center gap-2"
                    data-testid={`result-${r.id_formador}`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-[#0F1829] truncate">{r.nome}</p>
                      <p className="text-[10px] text-[#978D78] truncate">
                        {r.email || "—"} {r.nif && <span>· NIF: {r.nif}</span>}
                      </p>
                    </div>
                    {adding === r.id_formador ? (
                      <Loader2 className="animate-spin text-[#A8843C]" size={14} />
                    ) : (
                      <Plus size={14} className="text-[#A8843C]" />
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <footer className="p-4 border-t border-[#E6E1D6] flex justify-end">
          <button onClick={onClose} className="text-xs px-3 py-1.5 rounded border border-[#E6E1D6] hover:bg-[#FAF8F2]">
            Fechar
          </button>
        </footer>
      </div>
    </div>
  );
}
